import React, { useEffect, useRef, useState } from 'react';
import Box, { BoxProps } from '@mui/material/Box';

/**
 * Scroll-in arrival.
 *
 * The element stays at its final position in the layout and is only made visible
 * once it enters the viewport, so nothing reflows and the page has no scroll
 * jitter. `duoRise` carries the overshoot; the delay prop staggers a row.
 *
 * `once: true` is the default because a card that re-animates every time it
 * scrolls past is a distraction rather than a delight.
 */
export default function Reveal({
  delay = 0,
  animation = 'duoRise',
  duration = 520,
  children,
  sx,
  ...props
}: BoxProps & { delay?: number; animation?: 'duoRise' | 'duoPop'; duration?: number }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    /* No IntersectionObserver (or a very old engine): show the content rather
       than leaving the page permanently blank. */
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
          }
        });
      },
      /* Fires a little before the element is fully on screen, so the motion has
         finished by the time the reader's eye arrives. */
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      {...props}
      sx={{
        opacity: shown ? 1 : 0,
        animation: shown ? `${animation} ${duration}ms cubic-bezier(0.34, 1.4, 0.5, 1) ${delay}ms both` : 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
